<script setup lang="ts">
// Signal-health block on the Risk tab (spec 2026-10-23): one SignalHealthTile per visible fund,
// with an (i) legend. Which funds show is the host's `visibleFunds` (fund ids as the API's fundId).
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { api, type SignalHealthFund } from '../api/client'
import { todayLondonIso } from './fof/fofFormat'
import SignalHealthTile from './SignalHealthTile.vue'

const props = defineProps<{ visibleFunds: string[] }>()

const funds = ref<SignalHealthFund[]>([])
const error = ref<string | null>(null)
const legendOpen = ref(false)
const today = computed(() => todayLondonIso())
let timer: ReturnType<typeof setInterval> | null = null

const shown = computed(() => funds.value.filter(f => props.visibleFunds.includes(f.fundId)))

async function load() {
  try {
    const res = await api.signalHealth()
    funds.value = Array.isArray(res?.funds) ? res.funds : []
    error.value = null
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e)
  }
}

onMounted(() => { load(); timer = setInterval(load, 10 * 60_000) })
onUnmounted(() => { if (timer) clearInterval(timer) })
</script>

<template>
  <section class="sh-block" data-testid="signal-health-block">
    <div class="sh-header">
      <h3 class="sh-title">Signal health — edge outcome: does the signal still pay?</h3>
      <button
        type="button"
        class="kt-info-btn"
        :aria-expanded="legendOpen"
        aria-controls="sh-legend"
        :aria-label="legendOpen ? 'Hide explanation' : 'Show explanation'"
        data-testid="sh-info-toggle"
        @click="legendOpen = !legendOpen"
      >i</button>
    </div>

    <section v-if="legendOpen" id="sh-legend" class="kt-legend" role="region"
      aria-label="Signal health explanation" data-testid="sh-legend">
      <p class="kt-legend-lead"><b>What it measures.</b> Whether each fund's stale-NAV edge is intact: the reference
        future's move from the prior US close to 08:40 UK predicts the sign of the next official NAV move only while the
        fund's holdings still track that future and the manager still strikes on stale prices.</p>
      <p class="kt-legend-lead"><b>Companion section.</b> This measures whether the signal still pays, not how the NAV is
        struck. A valuer switching to live prices shows first in <i>Pricing source on the NAV tab</i>; this block
        catches the other failure, the holdings drifting off the reference, which Pricing source cannot see.</p>
      <p class="kt-legend-lead"><b>How it is calculated.</b> From the fund's events file, over the last 250 dealing days:
        fund β to the reference (slope of the day's NAV return on the reference's full-day move), signed bp/day (mean of
        sign(move) × next-day NAV return), hit rate (share of days the sign was right) and P(β&gt;0) (one-sided confidence
        that the pass-through into the next NAV is positive; HC0 robust). Grey values are the prior 750 days.</p>
      <p class="kt-legend-lead"><b>Thresholds.</b></p>
      <table class="sh-thresholds">
        <tbody>
          <tr><th>BGA</th><td>Red when β &lt; 0.7 and signed &lt; 15 bp/day (absolute, agreed 23 Oct 2026)</td></tr>
          <tr><th>Others</th><td>Red when β &lt; 0.6 × prior and signed &lt; 0.5 × prior</td></tr>
          <tr><th>Amber</th><td>One leg, P(β&gt;0) &lt; 0.90, fewer than 250 days, or a stale file</td></tr>
        </tbody>
      </table>
      <p class="kt-legend-lead">Recomputed once a day after the fund's strike.</p>
      <p class="kt-legend-lead"><b>Worked example.</b> BGA on 21 Oct 2026: β 0.63 vs prior 0.99, 12 bp vs 39 bp, hit
        55.1%, P(β&gt;0) 0.89 → both legs met → WATCH; acknowledged until 25 Dec 2026 pending the December reallocation
        review.</p>
    </section>

    <p v-if="error" class="sh-error" data-testid="sh-error">Signal-health data unavailable ({{ error }}).</p>
    <p v-else-if="shown.length === 0" class="sh-empty">No signal-health data yet.</p>

    <div class="sh-tiles">
      <SignalHealthTile v-for="f in shown" :key="f.fundId" :fund="f" :today="today" />
    </div>
  </section>
</template>

<style scoped>
.sh-block { margin: 1rem 0; }
.sh-header { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; }
.sh-title { margin: 0; font-size: 1rem; }
.sh-tiles { display: flex; flex-wrap: wrap; gap: 0.75rem; }
.sh-error { color: #d9534f; font-size: 0.85rem; }
.sh-empty { color: #888; font-size: 0.85rem; }
.sh-thresholds { border-collapse: collapse; margin: 0.2rem 0 0.6rem; font-size: 0.78rem; }
.sh-thresholds th, .sh-thresholds td { border: 1px solid #2a2a2a; padding: 0.15rem 0.5rem; text-align: left; }
/* kt-info-btn / kt-legend: same look as PricingSourcePanel's (i) legend (scoped copy). */
.kt-info-btn { display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; padding: 0;
  border-radius: 50%; background: transparent; color: #888; border: 1px solid #555;
  font-family: 'Times New Roman', Times, serif; font-style: italic; cursor: pointer; }
.kt-info-btn:hover, .kt-info-btn[aria-expanded="true"] { color: #e6edf3; border-color: #999; background: #ffffff10; }
.kt-info-btn:focus-visible { outline: 2px solid #58a6ff; outline-offset: 2px; }
.kt-legend { background: #141414; border: 1px solid #2a2a2a; border-radius: 6px; padding: 0.75rem 0.9rem;
  font-size: 0.8rem; color: #ccc; line-height: 1.5; margin-top: 0.5rem; }
.kt-legend-lead { margin: 0 0 0.65rem 0; color: #b3b3b3; }
</style>
